import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, DownloadIcon, Trash2Icon } from 'lucide-react';
import { FILE_TYPE_META } from '../../utils/fileTypes';
import { formatDateTime, formatSize } from '../../utils/format';
import type { LoanFile, SortKey, SortState } from '../../types/files';
interface FilesTableProps {
  files: LoanFile[];
  selectedIds: string[];
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  sort: SortState;
  compact: boolean;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  onSort: (key: SortKey) => void;
  onDownloadOne: (id: string) => void;
  onDeleteOne: (file: LoanFile) => void;
}
const COLUMNS: {
  key: SortKey;
  label: string;
  className: string;
}[] = [{
  key: 'title',
  label: 'Title',
  className: 'w-auto'
}, {
  key: 'owner',
  label: 'Owner',
  className: 'w-44 hidden md:table-cell'
}, {
  key: 'lastModified',
  label: 'Last Modified',
  className: 'w-52 hidden sm:table-cell'
}, {
  key: 'sizeKb',
  label: 'Size',
  className: 'w-24 text-right'
}];
export function FilesTable({
  files,
  selectedIds,
  allVisibleSelected,
  someVisibleSelected,
  sort,
  compact,
  onToggleRow,
  onToggleAll,
  onSort,
  onDownloadOne,
  onDeleteOne
}: FilesTableProps) {
  const rowPadding = compact ? 'py-1.5' : 'py-3';
  return <table className="w-full border-collapse text-sm" data-id="element-86" data-mp="322k386xjfb2e">
      <caption className="sr-only" data-id="element-87" data-mp="320ze4wtzskbx">
        Files attached to this loan application. Select files to download as a
        zip or delete.
      </caption>
      <thead data-id="element-88" data-mp="3tt85xnev0oi6">
        <tr className="border-b border-slate-200 bg-white text-left" data-id="element-89" data-mp="3tsyhi49gqpel">
          <th scope="col" className="w-10 px-4 py-2" data-id="element-90" data-mp="3fv05h5im7529">
            <input type="checkbox" checked={allVisibleSelected} ref={node => {
            if (node) node.indeterminate = someVisibleSelected;
          }} onChange={onToggleAll} aria-label="Select all files" className="h-4 w-4 rounded border-slate-400 text-[#0176d3] focus:ring-[#0176d3]" data-id="element-91" data-mp="3fwkvqixwc1ub" />
          </th>
          {COLUMNS.map(column => {
          const isSorted = sort.key === column.key;
          return <th key={column.key} scope="col" aria-sort={isSorted ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 ${column.className}`} data-id="element-92" data-mp="1gjq9n1j0quwa">
                <button type="button" onClick={() => onSort(column.key)} className={['inline-flex items-center gap-1 rounded hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]', column.key === 'sizeKb' ? 'ml-auto' : ''].join(' ')} data-id="element-93" data-mp="xlx5xx0kmgxu">
                  {column.label}
                  {isSorted && (sort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" aria-hidden="true" data-id="element-94" data-mp="2zgtlg8xf62il" /> : <ArrowDownIcon className="h-3 w-3" aria-hidden="true" data-id="element-95" data-mp="34bh9y7sfawx6" />)}
                </button>
              </th>;
        })}
          <th scope="col" className="w-24 px-3 py-2" data-id="element-96" data-mp="2blqsppvhvewu">
            <span className="sr-only" data-id="element-97" data-mp="2gjsckd0qvi9d">Row actions</span>
          </th>
        </tr>
      </thead>
      <tbody data-id="element-98" data-mp="2glmv5kojmyaa">
        {files.map(file => {
        const meta = FILE_TYPE_META[file.type];
        const Icon = meta.icon;
        const isSelected = selectedIds.includes(file.id);
        return <tr key={file.id} className={['group border-b border-slate-100 transition', isSelected ? 'bg-[#eef4ff]' : 'bg-white hover:bg-slate-50'].join(' ')} data-id="element-99" data-mp="sxq9tnt8zdz">
              <td className={`px-4 ${rowPadding} align-middle`} data-id="element-100" data-mp="3dlxvj1f26ch">
                <input type="checkbox" checked={isSelected} onChange={() => onToggleRow(file.id)} aria-label={`Select ${file.title}`} className="h-4 w-4 rounded border-slate-400 text-[#0176d3] focus:ring-[#0176d3]" data-id="element-101" data-mp="3uk6l0rocou48" />
              </td>
              <td className={`px-3 ${rowPadding} align-middle`} data-id="element-102" data-mp="ma52bv19z62p">
                <div className="flex items-center gap-2.5" data-id="element-103" data-mp="hbtrbgz8upiw">
                  <Icon className={`h-5 w-5 shrink-0 ${meta.iconClass}`} aria-hidden="true" data-id="element-104" data-mp="w1rk6a84c3ef" />
                  <div className="min-w-0" data-id="element-105" data-mp="r3zfsgqaeotc">
                    <button type="button" className="block max-w-full truncate text-left font-medium text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-106" data-mp="152fmw8s2ahc0">
                      {file.title}
                    </button>
                    <p className="text-xs text-slate-500 md:hidden" data-id="element-107" data-mp="1oohkjrimxo0a">
                      {file.owner} · {formatSize(file.sizeKb)}
                    </p>
                  </div>
                  <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600" data-id="element-108" data-mp="1fnb0j8gs1v2a">
                    {file.extension}
                  </span>
                </div>
              </td>
              <td className={`hidden px-3 ${rowPadding} align-middle text-slate-700 md:table-cell`} data-id="element-109" data-mp="15qex3h5qjz4l">
                {file.owner}
              </td>
              <td className={`hidden px-3 ${rowPadding} align-middle text-slate-600 sm:table-cell`} data-id="element-110" data-mp="vz24gnc454xi">
                <span className="block" data-id="element-111" data-mp="i0ay5n3hsnjx">{formatDateTime(file.lastModified)}</span>
                <span className="block text-xs text-slate-500" data-id="element-112" data-mp="mxuauikjks9r">
                  by {file.lastModifiedBy}
                </span>
              </td>
              <td className={`px-3 ${rowPadding} align-middle text-right tabular-nums text-slate-700`} data-id="element-113" data-mp="2opw5x2ydmt13">
                {formatSize(file.sizeKb)}
              </td>
              <td className={`px-3 ${rowPadding} align-middle`} data-id="element-114" data-mp="2ftdc7oibn13g">
                <div className="flex items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100" data-id="element-115" data-mp="2axy47d6zvzey">
                  <button type="button" onClick={() => onDownloadOne(file.id)} aria-label={`Download ${file.title}`} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-200 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-116" data-mp="33lc0ra6s6syj">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-117" data-mp="1i8920wijttbr" />
                  </button>
                  <button type="button" onClick={() => onDeleteOne(file)} aria-label={`Delete ${file.title}`} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-red-100 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-118" data-mp="184kgzbque5x6">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-119" data-mp="1qv1ui933suxy" />
                  </button>
                </div>
              </td>
            </tr>;
      })}
      </tbody>
    </table>;
}